/**
 * Pure derivation: proposal actions + a live selection predicate →
 * Record<entityId, PreviewOverlay> for the CRM preview overlay system.
 *
 * Output is keyed by entity id (the version-stable frozen UUID) — this module
 * never keys on `action.id`. Selection is delegated entirely to the
 * `isActionSelected` predicate (issue #11922 Decision 1), so the overlay is
 * re-derived fresh on every call: there is no action-id cache to orphan when
 * selection changes.
 */

import type {
  PreviewOverlay,
  ProjectedAssociationOverlay,
} from '@/lib/crm/preview';
import type { ProposedAction } from '@/lib/proposals/core/types';
import {
  isAssociationAddedPayload,
  isAssociationRemovedPayload,
  isObjectCreatedPayload,
  isObjectDeletedPayload,
  isPropertyUpdatePayload,
} from '@/lib/proposals/core/types';
import { unwrapPropertyValue } from '@/types/crm';

import {
  isTypedCreateAction,
  projectTypedCreatePayload,
} from './projectTypedCreatePayload';

function unwrapProps(props: unknown): Record<string, unknown> {
  if (!props || typeof props !== 'object') {
    // guards a present-but-null `properties`
    return {};
  }
  const out: Record<string, unknown> = {};
  for (const [k, val] of Object.entries(props as Record<string, unknown>)) {
    out[k] = unwrapPropertyValue(val);
  }
  return out;
}

type ContributionKind =
  | 'create'
  | 'update'
  | 'assoc_added'
  | 'assoc_removed'
  | 'deleted';

interface Contribution {
  action: ProposedAction;
  selected: boolean;
  kind: ContributionKind;
}

interface EntityAccumulator {
  entityTypeName: string;
  isCreate: boolean;
  contributions: Contribution[];
}

interface SurvivingAssociation {
  source: string;
  target: string;
  association_type_name: string;
  /** True when the proposal REMOVES this edge rather than adding it. */
  removed: boolean;
}

function associationKey(a: ProjectedAssociationOverlay): string {
  return `${a.association_type_name}|${a.target_entity_id}`;
}

/**
 * Resolves an association type's INVERSE type name
 * (`reverse_association_type_name` in the schema). Supplied by the caller
 * because this module is deliberately schema-free.
 */
export type ReverseAssociationTypeResolver = (
  associationTypeName: string
) => string | undefined;

export function derivePreviewOverlays(
  actions: ProposedAction[],
  isActionSelected: (action: ProposedAction) => boolean,
  resolveReverseAssociationTypeName?: ReverseAssociationTypeResolver
): Record<string, PreviewOverlay> {
  const sorted = [...actions].sort(
    (a, b) => a.execution_order - b.execution_order
  );

  const accumulators = new Map<string, EntityAccumulator>();

  function getAccumulator(
    entityId: string,
    entityTypeName: string
  ): EntityAccumulator {
    let acc = accumulators.get(entityId);
    if (!acc) {
      acc = { entityTypeName, isCreate: false, contributions: [] };
      accumulators.set(entityId, acc);
    }
    return acc;
  }

  for (const action of sorted) {
    const { payload } = action;

    if (isTypedCreateAction(action)) {
      // A typed create (`create_automation`, `create_validation_rule`) is a
      // creation like `ahoy_object_created`, with the payload→property
      // mapping delegated to the one module that knows it. Same
      // already-committed skip as below: once executed, the real record must
      // hydrate instead of a synthetic stub.
      if (action.execution_result?.status === 'success') {
        continue;
      }
      const projected = projectTypedCreatePayload(action);
      if (!projected) {
        continue;
      }
      const selected = isActionSelected(action);
      const acc = getAccumulator(projected.entityId, projected.entityTypeName);
      acc.isCreate = true;
      acc.contributions.push({ action, selected, kind: 'create' });
    } else if (isObjectCreatedPayload(payload)) {
      if (action.execution_result?.status === 'success') {
        // Entity is already committed; a create overlay would set
        // suppressFetch and render a synthetic stub instead of the real
        // record. Skip so the real object hydrates. Key on
        // execution_result.status, not the "when it ran" timestamp — a
        // 'skipped' create has that timestamp set but was never created
        // and must still project.
        continue;
      }
      const selected = isActionSelected(action);
      const acc = getAccumulator(payload.entity_id, payload.entity_type_name);
      acc.isCreate = true;
      acc.contributions.push({ action, selected, kind: 'create' });
    } else if (isPropertyUpdatePayload(payload)) {
      const selected = isActionSelected(action);
      const acc = getAccumulator(payload.entity_id, payload.entity_type_name);
      acc.contributions.push({ action, selected, kind: 'update' });
    } else if (isAssociationAddedPayload(payload)) {
      const selected = isActionSelected(action);
      const acc = getAccumulator(payload.entity_id, payload.entity_type_name);
      acc.contributions.push({ action, selected, kind: 'assoc_added' });
    } else if (isAssociationRemovedPayload(payload)) {
      const selected = isActionSelected(action);
      const acc = getAccumulator(payload.entity_id, payload.entity_type_name);
      acc.contributions.push({ action, selected, kind: 'assoc_removed' });
    } else if (isObjectDeletedPayload(payload)) {
      const selected = isActionSelected(action);
      const acc = getAccumulator(payload.entity_id, payload.entity_type_name);
      acc.contributions.push({ action, selected, kind: 'deleted' });
    }
  }

  const overlays: Record<string, PreviewOverlay> = {};
  const survivingAssociations: SurvivingAssociation[] = [];

  for (const [entityId, acc] of accumulators) {
    const anySelected = acc.contributions.some(c => c.selected);
    const excluded = !anySelected;
    const foldSet = anySelected
      ? acc.contributions.filter(c => c.selected)
      : acc.contributions;

    let properties: Record<string, unknown> = {};
    for (const c of foldSet) {
      const { payload } = c.action;
      if (isTypedCreateAction(c.action)) {
        // Already property-keyed and unwrapped — no `unwrapProps` pass.
        const projected = projectTypedCreatePayload(c.action);
        if (projected) {
          properties = { ...properties, ...projected.properties };
        }
      } else if (isObjectCreatedPayload(payload)) {
        properties = {
          ...properties,
          ...unwrapProps(payload.payload.properties),
        };
      } else if (isPropertyUpdatePayload(payload)) {
        properties = {
          ...properties,
          ...unwrapProps(payload.payload.properties),
        };
      }
    }

    const associations: ProjectedAssociationOverlay[] = [];
    const removedAssociations: ProjectedAssociationOverlay[] = [];
    for (const c of foldSet) {
      const { payload } = c.action;
      if (isAssociationRemovedPayload(payload)) {
        // A removal cannot be expressed as a positive association, but the
        // target still has to RENDER — struck through and marked — so the user
        // can see what the proposal takes away. Kept in its own list.
        const { association_type_name, target_entity_id } = payload.payload;
        removedAssociations.push({ association_type_name, target_entity_id });
        // A removal is a directed edge like an addition, so it needs the same
        // reverse pass: without it the change is marked on the source but
        // invisible on the target (the company shows the contact as though
        // nothing were happening).
        survivingAssociations.push({
          source: entityId,
          target: target_entity_id,
          association_type_name,
          removed: true,
        });
        continue;
      }
      if (!isAssociationAddedPayload(payload)) {
        continue;
      }
      const { association_type_name, target_entity_id } = payload.payload;
      associations.push({ association_type_name, target_entity_id });
      survivingAssociations.push({
        source: entityId,
        target: target_entity_id,
        association_type_name,
        removed: false,
      });
    }

    overlays[entityId] = {
      entityId,
      entityTypeName: acc.entityTypeName,
      kind: acc.isCreate ? 'create' : 'update',
      properties,
      associations: associations.length > 0 ? associations : undefined,
      removedAssociations:
        removedAssociations.length > 0 ? removedAssociations : undefined,
      excluded,
    };
  }

  // Reverse pass: an added association is a directed edge (source → target).
  // mergeProjection only ever reads the SOURCE entity's overlay for a forward
  // edge, so without this pass the target entity's own overlay would never
  // reflect the relationship (see actionMerging.ts:105-127 for the same
  // source-only-index bug in the sibling merge utility).
  //
  // The edge must be re-typed, not copied: viewed from the target the
  // relationship IS a different association type, with its own label. A
  // contact→company edge is `ahoy_contact_to_company` ("Works At"); the same
  // relationship on the company is `ahoy_company_to_contact` ("Employees").
  // Reusing the forward name renders the company's own row labelled "Works At".
  for (const {
    source,
    target,
    association_type_name,
    removed,
  } of survivingAssociations) {
    const targetOverlay = overlays[target];
    if (!targetOverlay) {
      // Existing entity not created/edited in this proposal — its type is
      // unknown, so mergeProjection could not project it. Intentionally skipped.
      continue;
    }
    const reverseTypeName = resolveReverseAssociationTypeName?.(
      association_type_name
    );
    if (!reverseTypeName) {
      // No resolver, or the schema has no inverse for this type. Emitting the
      // forward name here would put a wrong label in front of the user, which
      // is worse than omitting the row — so skip it.
      continue;
    }
    const reverseEdge = {
      association_type_name: reverseTypeName,
      target_entity_id: source,
    };
    if (removed) {
      targetOverlay.removedAssociations = [
        ...(targetOverlay.removedAssociations ?? []),
        reverseEdge,
      ];
    } else {
      targetOverlay.associations = [
        ...(targetOverlay.associations ?? []),
        reverseEdge,
      ];
    }
  }

  // Determinism: stable sort + de-dup so the store's JSON.stringify diff
  // (createPreviewStore.ts:72) never spuriously flags a change.
  for (const overlay of Object.values(overlays)) {
    if (!overlay.associations || overlay.associations.length === 0) {
      overlay.associations = undefined;
      continue;
    }
    const seen = new Set<string>();
    const deduped: ProjectedAssociationOverlay[] = [];
    for (const a of overlay.associations) {
      const key = associationKey(a);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      deduped.push(a);
    }
    deduped.sort((a, b) => associationKey(a).localeCompare(associationKey(b)));
    overlay.associations = deduped;
  }

  return overlays;
}
